import { yupResolver } from '@hookform/resolvers/yup';
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { postSignIn } from '../../../api/auth';
import Button from '../../../components/UI/Button';
import { HookForm } from '../../../components/UI/HookForm/Form';
import { HookInput } from '../../../components/UI/HookForm/Form/Input';
import { HookFormProvider } from '../../../components/UI/HookForm/Provider';
import { useCurrentAccountData } from '../../../hooks/ReactQueryHooks/useCurrentAccountQuery';
import { authSelector } from '../../../store';
import { SignInUser } from '../../../store/Auth';
import { sanitizeResponseError } from '../../../utils';
import { validationSchema } from './validationSchema';

interface SignInInterface {
  email: string;
  password: string;
}

function SignIn({ slideHandler }: { slideHandler: () => void }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const auth = useSelector(authSelector);
  const [submitError, setSubmitError] = useState('');
  const fetchCurrentClientAccount = useCurrentAccountData({});
  const currentClientAccount = fetchCurrentClientAccount?.data?.data;

  const signInUser = useMutation(
    (payload: SignInInterface) => postSignIn(payload.email, payload.password),
    {
      onSuccess: () => handleSignInSuccess(),
      onError: (error: any) => handleSignInError(error),
    }
  );

  const handleSignInSuccess = () => {
    dispatch(SignInUser());
    let redirectPath = '/app/dashboard';
    if (currentClientAccount?.subdomain === 'sageadmin')
      redirectPath = '/app/clients';
    navigate(redirectPath, { replace: true });
  };

  const handleSignInError = (error: any) => {
    const { status } = error.response;
    if (status === 422) {
      setSubmitError('Invalid params');
    } else if (status === 401) {
      setSubmitError(sanitizeResponseError(error));
    } else {
      setSubmitError(
        'We could not sign you in at this moment. Please try again later'
      );
    }
  };

  const onSubmit = async (data: SignInInterface) => {
    signInUser.mutate(data);
  };

  const renderSubmitError = () => {
    return <span className='text-red-500'>{submitError}</span>;
  };

  const renderCurrentUserError = () => {
    return <span className='text-red-500'>{auth.errorMsg}</span>;
  };

  return (
    <HookFormProvider resolver={yupResolver(validationSchema)} mode='all'>
      <HookForm
        noDefaultClass
        className='bg-white md:w-2/3 lg:w-2/5 max-w-sm shadow-md rounded-md px-8 pt-6 pb-8 border-t-2'
        onSubmit={onSubmit}
      >
        <h2 className='mt-0'>Sign In</h2>
        {auth.error && auth.signInAttempted && (
          <p className='mb-2'>{renderCurrentUserError()}</p>
        )}
        {signInUser.error && <p className='mb-2'>{renderSubmitError()}</p>}

        <div className='space-y-4'>
          <HookInput
            label='Email'
            name='email'
            type='email'
            placeholder='Enter your email'
          />
          <HookInput
            label='Password'
            name='password'
            type='password'
            placeholder='Enter your password'
          />
        </div>
        <p
          className='text-xs text-neutralColor-normal hover:text-secondaryColor-normal cursor-pointer mb-8 mt-2'
          onClick={slideHandler}
        >
          Forgot your password ?
        </p>
        <Button
          customClassName='w-full'
          label={`${signInUser.isLoading ? 'Please Wait...' : 'Sign In'}`}
          type='submit'
          disabled={signInUser.isLoading}
          btnStyle='btn-primary'
        />
      </HookForm>
    </HookFormProvider>
  );
}

export default SignIn;
